import gql from "graphql-tag";

import type {CurationNode, TopicChild} from "@khan/class-types";
// TODO(somewhatabstract, JIRA-XXX9): Improve our patterns so that we
// can pass a `gqlFetch` from the react components.
// DO NOT COPY - bad example of how to get a `gqlFetch`.
import {Persist, standaloneGqlFetch} from "@khan/data-gql";
import {gqlOp} from "@khan/data-gql-core";

import type {contentTabThreeLevelQueryVariables as ThreeLevelVariables} from "./__graphql-types__/contentTabThreeLevelQuery.ts";
import type {contentTabTwoLevelQueryVariables as TwoLevelVariables} from "./__graphql-types__/contentTabTwoLevelQuery.ts";

// Fields necessary for rendering quizzes and unit tests
const QUIZ_AND_TEST_FIELDS_FRAGMENT = gql`
    fragment QuizAndTestFields on LearnableContent {
        ... on TopicQuiz {
            coveredTutorials {
                id
                title: translatedTitle
            }
            defaultUrlPath
            expectedDoNCount: exerciseLength
            index
        }
        ... on TopicUnitTest {
            defaultUrlPath
            expectedDoNCount: exerciseLength
        }
    }
`;

interface TwoThreeLevelData {
    courseById: {
        id: string;
        children: Array<TopicChild> | null | undefined;
        courseChallenge: TopicChild | null | undefined;
        domain: CurationNode;
    };
}

// [TWO|THREE]_LEVEL_QUERY returns information about a given course, and two or
// three layers of that course's children.
// (THREE_LEVEL_QUERY takes meaningfully longer to run, so we kick off both in
// parallel so that TWO_LEVEL_QUERY gets some data on the screen whlie
// THREE_LEVEL_QUERY is processing.)
//
// NOTE: Courses *usually* only contain Units. But in some edge cases, a Course
// can contain:
// * Another Course! We handle this by requesting only basic details (contained
//   in the CourseFields fragment) for this sub-course, since we'll do another
//   round of [TWO|THREE]_LEVEL_QUERY-ing if/when the user opens that Course.
// * A Lesson. We handle this by including the Lesson fragment as a child of
//   Course (contained in the LessonAndChildrenFields fragment).
const TWO_LEVEL_QUERY = gqlOp<TwoThreeLevelData, TwoLevelVariables>(gql`
    query contentTabTwoLevelQuery(
        $courseId: String!
        $region: String!
        $locale: String!
    ) {
        courseById(id: $courseId) {
            id
            children: curatedChildren {
                ...CourseFields
                ...LessonAndChildrenFields
                ... on Unit {
                    id
                    description
                    iconPath
                    key
                    kind
                    mappedStandardIds(locale: $locale, region: $region)
                    title: translatedTitle
                    children: allOrderedChildren {
                        __typename
                        ...LearnableContentFields
                        ...QuizAndTestFields
                        ... on Lesson {
                            id
                            description
                            iconPath
                            key
                            kind
                            mappedStandardIds(locale: $locale, region: $region)
                            title: translatedTitle
                        }
                    }
                }
            }
            courseChallenge {
                id
                contentId
                defaultUrlPath
                description
                kind
                mappedStandardIds(locale: $locale, region: $region)
                title: translatedTitle
                expectedDoNCount: exerciseLength
            }
            domain: parent {
                id
                contentKind
            }
        }
    }
    fragment CourseFields on Course {
        ... on Course {
            id
            description
            iconPath
            key
            kind
            mappedStandardIds(locale: $locale, region: $region)
            title: translatedTitle
        }
    }
    fragment LearnableContentFields on LearnableContent {
        contentId
        description
        kind
        mappedStandardIds(locale: $locale, region: $region)
        nodeUrl: urlWithinTopic
        slug
        title: translatedTitle
    }
    fragment LessonAndChildrenFields on Lesson {
        ... on Lesson {
            id
            description
            iconPath
            key
            kind
            mappedStandardIds(locale: $locale, region: $region)
            title: translatedTitle
            children {
                __typename
                ...LearnableContentFields
                ... on Video {
                    duration
                    imageUrl
                    translatedYoutubeId
                    youtubeId
                }
                ... on Exercise {
                    expectedDoNCount: exerciseLength(useDefault: true)
                    problemTypeKind
                }
            }
        }
    }
    ${QUIZ_AND_TEST_FIELDS_FRAGMENT}
`);

const THREE_LEVEL_QUERY = gqlOp<TwoThreeLevelData, ThreeLevelVariables>(gql`
    query contentTabThreeLevelQuery(
        $courseId: String!
        $region: String!
        $locale: String!
    ) {
        courseById(id: $courseId) {
            id
            children: curatedChildren {
                ...CourseFieldsThreeLevels
                ...LessonAndChildrenFieldsThreeLevels
                ... on Unit {
                    id
                    description
                    iconPath
                    key
                    kind
                    mappedStandardIds(locale: $locale, region: $region)
                    title: translatedTitle
                    children: allOrderedChildren {
                        __typename
                        ...LearnableContentFieldsThreeLevels
                        ...QuizAndTestFields
                        ...LessonAndChildrenFieldsThreeLevels
                    }
                }
            }
            courseChallenge {
                id
                contentId
                defaultUrlPath
                description
                kind
                mappedStandardIds(locale: $locale, region: $region)
                title: translatedTitle
                expectedDoNCount: exerciseLength
            }
            domain: parent {
                id
                contentKind
            }
        }
    }
    fragment CourseFieldsThreeLevels on Course {
        ... on Course {
            id
            description
            iconPath
            key
            kind
            mappedStandardIds(locale: $locale, region: $region)
            title: translatedTitle
        }
    }
    fragment LearnableContentFieldsThreeLevels on LearnableContent {
        contentId
        description
        kind
        mappedStandardIds(locale: $locale, region: $region)
        nodeUrl: urlWithinTopic
        slug
        title: translatedTitle
    }
    fragment LessonAndChildrenFieldsThreeLevels on Lesson {
        ... on Lesson {
            id
            description
            iconPath
            key
            kind
            mappedStandardIds(locale: $locale, region: $region)
            title: translatedTitle
            children {
                __typename
                ...LearnableContentFieldsThreeLevels
                ... on Video {
                    duration
                    imageUrl
                    translatedYoutubeId
                    youtubeId
                }
                ... on Exercise {
                    expectedDoNCount: exerciseLength(useDefault: true)
                    problemTypeKind
                }
            }
        }
    }
    ${QUIZ_AND_TEST_FIELDS_FRAGMENT}
`);

// TODO(dhruv, CLASS-2953): It is expensive to load mappedStandards data for
// all of the content, so wait to do so until after the page loads.
export const getTopicChildren = async (
    courseId: string,
    region: string,
    locale: string,
    fetchThreeLevels = false,
): Promise<TwoThreeLevelData["courseById"]> => {
    const query = fetchThreeLevels ? THREE_LEVEL_QUERY : TWO_LEVEL_QUERY;

    // TODO(somewhatabstract, JIRA-XXX9): Improve our Redux patterns so that we
    // can pass a valid `gqlFetch` from the react components.
    // eslint-disable-next-line no-restricted-syntax -- Port from classic
    const data = await standaloneGqlFetch(query, {
        variables: {courseId, region, locale},
        context: {fastlyCaching: Persist.UntilPublish},
    });

    const children = data.courseById.children;

    const courseChallenge = data.courseById.courseChallenge;
    if (courseChallenge) {
        children?.push(courseChallenge);
    }

    return {
        id: courseId,
        children: children,
        courseChallenge: data.courseById.courseChallenge,
        domain: data.courseById.domain,
    };
};
